import { z } from 'zod';

import type { ReimbursementIntakeField } from '../ap/reimbursement.js';
import type { ApprovalGlobalSettings } from '../approval-settings.js';
import type { ArCollectionSettings } from '../ar-collection.js';
import type { BillingArForecastSettings } from '../billing-ar-forecast-settings.js';
import type { BillingSaasMetricsSettings } from '../billing-saas-metrics-settings.js';
import type { BudgetGlobalSettings } from '../budget.js';
import type { SettingType } from '../generated/enums.js';
import type { ProcurementGlobalSettings } from '../procurement-settings.js';

export const BusinessContextSettingSchema = z.string().max(2000);
export type BusinessContextSetting = z.infer<typeof BusinessContextSettingSchema>;

/**
 * AP bill ingestion settings (tenant setting AP_BILL_INGESTION_SETTINGS).
 *
 * `defaultEntityId` is applied at the start of bundle processing when no tenant
 * entity can be identified from the documents, e-mail body, or user instructions.
 */
export const ApBillIngestionSettingsSchema = z.object({
  defaultEntityId: z.string().uuid().nullable(),
});
export type ApBillIngestionSettings = z.infer<typeof ApBillIngestionSettingsSchema>;

export const DEFAULT_AP_BILL_INGESTION_SETTINGS: ApBillIngestionSettings = {
  defaultEntityId: null,
};

/**
 * AR invoice approval settings (tenant setting AR_INVOICE_APPROVAL_SETTINGS).
 *
 * `autoApproveExternallyBilledInvoices`: when on, an invoice is approved right after it is
 * created if every line's billing service has its NF issued outside Alchemy
 * (`nfIssuer = EXTERNAL`). The approval is the same one a person runs; a refusal leaves the
 * invoice in DRAFT. Changing it requires `ar:approve` granted explicitly.
 */
export const ArInvoiceApprovalSettingsSchema = z
  .object({
    autoApproveExternallyBilledInvoices: z.boolean(),
  })
  .strict();
export type ArInvoiceApprovalSettings = z.infer<typeof ArInvoiceApprovalSettingsSchema>;

export const DEFAULT_AR_INVOICE_APPROVAL_SETTINGS: ArInvoiceApprovalSettings = {
  autoApproveExternallyBilledInvoices: false,
};

/**
 * HCM Payroll GL Settings (tenant setting HCM_PAYROLL_GL_SETTINGS)
 *
 * Stored in sys_tenant_setting: one row per tenant, setting_type = 'HCM_PAYROLL_GL_SETTINGS',
 * setting_config = JSON { splitBy?, payrollResultRootAccountIds? }.
 *
 * - splitBy: which dimension to use for result (debit) split; required before approve.
 * - payrollResultRootAccountIds: allowed expense/result GL root account IDs for CC/Dept mapping
 *   (curated by accountant; manager picks one per dept/CC on the dimension mapping page).
 *   Liability/payable accounts live in system_account_mapping and must not be listed here.
 *
 * The payroll payable account IDs (salary liability, INSS/FGTS/IRRF, etc.) live in
 * system_account_mapping (Contas do sistema), not here.
 */
export type HcmPayrollSplitBy = 'COST_CENTER' | 'DEPARTMENT';

/** When salary AP payments are due relative to the payroll period. */
export const PayrollSalaryPaymentRule = {
  /** 5th business day of the month following the journal date (default). */
  FIFTH_BUSINESS_DAY: 'FIFTH_BUSINESS_DAY',
  /** Last calendar day of the payroll reference month; previous business day if weekend. */
  LAST_DAY_OF_MONTH: 'LAST_DAY_OF_MONTH',
  /** 1st calendar day of the month following the journal; next business day if weekend. */
  FIRST_DAY_OF_NEXT_MONTH: 'FIRST_DAY_OF_NEXT_MONTH',
  /** 5th calendar day of the month following the journal; next business day if weekend. */
  FIFTH_CALENDAR_DAY: 'FIFTH_CALENDAR_DAY',
  /** @deprecated Alias of LAST_DAY_OF_MONTH — kept for stored tenant settings. */
  LAST_DAY_OF_REFERENCE_MONTH: 'LAST_DAY_OF_REFERENCE_MONTH',
} as const;
export type PayrollSalaryPaymentRule =
  (typeof PayrollSalaryPaymentRule)[keyof typeof PayrollSalaryPaymentRule];

/** Where a payroll deduction transit credits after debiting Salários a Pagar. */
export const PayrollDeductionDestinationType = {
  TAX: 'TAX',
  REPASSE: 'REPASSE',
  EXPENSE_RECOVERY: 'EXPENSE_RECOVERY',
  ASSET_SETTLEMENT: 'ASSET_SETTLEMENT',
} as const;
export type PayrollDeductionDestinationType =
  (typeof PayrollDeductionDestinationType)[keyof typeof PayrollDeductionDestinationType];

/** How the transit credit lines are emitted for a mapped deduction rubric. */
export const PayrollDeductionGrouping = {
  /** Sum into one credit line per destination GL account (default). */
  GROUPED: 'GROUPED',
  /** One credit line per rubric occurrence (e.g. alimony per employee). */
  INDIVIDUAL: 'INDIVIDUAL',
} as const;
export type PayrollDeductionGrouping =
  (typeof PayrollDeductionGrouping)[keyof typeof PayrollDeductionGrouping];

/** AP bill subtype for payroll-generated bills (`ApBill.customFields.payrollSubType`). */
export const PayrollBillSubType = {
  SALARY: 'SALARY',
  ADVANCE: 'ADVANCE',
  THIRTEENTH: 'THIRTEENTH',
  VACATION: 'VACATION',
  PRO_LABORE: 'PRO_LABORE',
  RPA: 'RPA',
  TERMINATION_NET: 'TERMINATION_NET',
  ALIMONY: 'ALIMONY',
  CONSIGNED: 'CONSIGNED',
  /** Contribuição / mensalidade sindical withheld for the union. */
  UNION: 'UNION',
  TAX: 'TAX',
} as const;
export type PayrollBillSubType = (typeof PayrollBillSubType)[keyof typeof PayrollBillSubType];

/** Provider rubric code → GL destination for non-tax deduction transits. */
export interface PayrollDeductionRubricMapping {
  providerCode: string;
  /**
   * Normalized rubric name (`normalizePayrollRubricName`) the tenant confirmed. Rubric identity
   * is provider family + side + name (a provider such as Tako has no rubric code), so a name
   * entry wins over a `providerCode` entry. `providerCode` stays set on every entry (legacy
   * rows carry no name; name entries also carry one real code, or a name-derived one).
   */
  rubricName?: string;
  /** Entity override. Omit together with versionId for the tenant-wide fallback. */
  entityId?: string;
  /** CoA-version override within entityId. Requires entityId. */
  versionId?: string;
  destinationType: PayrollDeductionDestinationType;
  /** Required for REPASSE / EXPENSE_RECOVERY / ASSET_SETTLEMENT transits. */
  glAccountId?: string;
  /** Credit-line emission; defaults to GROUPED when omitted. */
  grouping?: PayrollDeductionGrouping;
  /** When true, approve also creates an AP bill for the withheld amount (repasse to third party). */
  generatesBill?: boolean;
  /** Bill subtype for AP filtering; required when generatesBill is true. */
  billSubType?: PayrollBillSubType;
  /** Third-party payee (e.g. pension beneficiary vendor, consignor bank) for generated bills. */
  payeeVendorId?: string;
}

/**
 * Side of a payroll rubric a tenant classified by hand (ADR 0022 decision 4). Deductions are
 * not here: their class lives in `deductionRubricMappings` (`destinationType`).
 */
export const PayrollRubricOverrideSide = {
  EARNING: 'EARNING',
  EMPLOYER_CHARGE: 'EMPLOYER_CHARGE',
} as const;
export type PayrollRubricOverrideSide =
  (typeof PayrollRubricOverrideSide)[keyof typeof PayrollRubricOverrideSide];

/**
 * Tenant confirmation "provider rubric code → class" for an earning or an employer charge.
 * It wins over the central rubric dictionary and over the name classifier. An `entityId`
 * entry wins over the tenant-wide one for that entity.
 */
export interface PayrollRubricClassOverride {
  providerCode: string;
  /**
   * Normalized rubric name (`normalizePayrollRubricName`). Rubric identity is provider family +
   * side + name, so a name entry wins over a `providerCode` entry (legacy rows carry no name).
   */
  rubricName?: string;
  side: PayrollRubricOverrideSide;
  /**
   * EARNING: a payroll nature (`HcmPayrollRubricType`) or `ADVANCE` for the salary advance
   * paid on the adiantamento folha (debits the employee-advance asset, not an expense).
   * `SOCIAL_SECURITY_BENEFIT` (salário-maternidade / família) debits the INSS payable.
   * EMPLOYER_CHARGE: `INSS_EMPLOYER` | `INSS_RAT` | `INSS_TERCEIROS` | `FGTS`.
   */
  class: string;
  /** Entity override. Omit for the tenant-wide entry. */
  entityId?: string;
}

/**
 * Selects the effective earning / employer-charge override for one rubric. Lookup order:
 * override by normalized name (entity entry, then tenant-wide) → override by provider code
 * (entity entry, then tenant-wide). `rubricName` must already be normalized by the caller
 * (`normalizePayrollRubricName`); entries store it normalized.
 */
export function resolveRubricClassOverride(
  overrides: readonly PayrollRubricClassOverride[] | null | undefined,
  key: {
    providerCode?: string | null;
    rubricName?: string | null;
    side: PayrollRubricOverrideSide;
    entityId?: string | null;
  },
): PayrollRubricClassOverride | null {
  const providerCode = key.providerCode?.trim() ?? '';
  const rubricName = key.rubricName?.trim() ?? '';
  if (!providerCode && !rubricName) return null;

  const pick = (
    matches: (override: PayrollRubricClassOverride) => boolean,
  ): PayrollRubricClassOverride | null => {
    let tenantWide: PayrollRubricClassOverride | null = null;
    for (const override of overrides ?? []) {
      if (override.side !== key.side || !matches(override)) continue;
      if (override.entityId == null) {
        tenantWide ??= override;
      } else if (key.entityId && override.entityId === key.entityId) {
        return override;
      }
    }
    return tenantWide;
  };

  return (
    (rubricName ? pick((o) => o.rubricName?.trim() === rubricName) : null) ??
    (providerCode ? pick((o) => o.providerCode?.trim() === providerCode) : null)
  );
}

/**
 * Selects one effective mapping per provider code for a resolved payroll accounting scope.
 * Entity + version wins over entity, which wins over the legacy tenant-wide entry.
 */
export function resolveDeductionRubricMappings(
  mappings: readonly PayrollDeductionRubricMapping[] | null | undefined,
  scope: { entityId: string; versionId: string },
): PayrollDeductionRubricMapping[] {
  const selected = new Map<
    string,
    { mapping: PayrollDeductionRubricMapping; specificity: number }
  >();

  for (const mapping of mappings ?? []) {
    const providerCode = mapping.providerCode.trim();
    if (!providerCode) continue;

    let specificity = 0;
    if (mapping.entityId === scope.entityId && mapping.versionId === scope.versionId) {
      specificity = 3;
    } else if (mapping.entityId === scope.entityId && mapping.versionId == null) {
      specificity = 2;
    } else if (mapping.entityId == null && mapping.versionId == null) {
      specificity = 1;
    }
    if (specificity === 0) continue;

    const current = selected.get(providerCode);
    if (!current || specificity > current.specificity) {
      selected.set(providerCode, { mapping, specificity });
    }
  }

  return [...selected.values()].map(({ mapping }) => mapping);
}

/** Stable comparison key for the posting behavior of one effective mapping set. */
export function deductionRubricMappingsPostingSignature(
  mappings: readonly PayrollDeductionRubricMapping[],
): string {
  return JSON.stringify(
    mappings
      .map((mapping) => ({
        providerCode: mapping.providerCode.trim(),
        destinationType: mapping.destinationType,
        glAccountId: mapping.glAccountId ?? null,
        grouping: mapping.grouping ?? null,
        generatesBill: mapping.generatesBill ?? false,
        billSubType: mapping.billSubType ?? null,
        payeeVendorId: mapping.payeeVendorId ?? null,
      }))
      .sort((left, right) => left.providerCode.localeCompare(right.providerCode)),
  );
}

/**
 * One entity's dated encargos rate override (ALC-2545). `effectiveFrom` is a
 * month key (`YYYY-MM`); the override applies from that month onward until a
 * later override for the same entity takes effect. Every rate is required —
 * an entity override is all-or-nothing, never a partial patch over the
 * tenant default.
 */
export interface EntityEncargosRateOverride {
  entityId: string;
  /** Month key (`YYYY-MM`) this rate takes effect from, inclusive. */
  effectiveFrom: string;
  /** Cota patronal previdenciária (CPP). */
  cpp: number;
  /** RAT ajustado (RAT × FAP). */
  ratFap: number;
  /** Contribuições a terceiros (sistema S etc.). */
  terceiros: number;
  /** FGTS. */
  fgts: number;
}

/**
 * Encargo rates applied over INTERNAL provision accruals (férias/13º).
 * Source of truth when set — replaces the derived employerCharges/gross
 * ratio and the 28% fallback, which are known to understate the liability.
 * Fractions of 1 (e.g. CPP 20% = 0.20).
 *
 * Two levels (ALC-2545): the flat `cpp`/`ratFap`/`terceiros`/`fgts` fields
 * are the TENANT-level default (legacy shape, kept for backward
 * compatibility with existing single-entity tenants). `byEntity` carries
 * per-entity, vigência-dated overrides — a scope with Brazilian entities on
 * different RAT/FAP or terceiros rates must not collapse onto one number.
 * Resolution order per entity: entity override effective as of the report
 * date → tenant default → statutory fallback. See
 * `packages/services/src/payroll-encargos-policy/CONTEXT.md`.
 */
export interface PayrollEncargosRates {
  /** Cota patronal previdenciária (CPP). Tenant-level default. */
  cpp?: number;
  /** RAT ajustado (RAT × FAP). Tenant-level default. */
  ratFap?: number;
  /** Contribuições a terceiros (sistema S etc.). Tenant-level default. */
  terceiros?: number;
  /** FGTS. Tenant-level default. */
  fgts?: number;
  /** Per-entity, vigência-dated overrides. */
  byEntity?: EntityEncargosRateOverride[];
}

/** One stream's declared payment day (ALC-3560). `dayOfMonth` is clamped to the competência's actual length. */
export interface PaymentCalendarStream {
  enabled: boolean;
  /** 1–31. */
  dayOfMonth: number;
}

/**
 * Declared payment pattern for non-salary payroll streams (ALC-3560). Salary keeps
 * `salaryPaymentRule` — this setting exists only for the streams that rule doesn't cover.
 * Two levels, same shape as `PayrollEncargosRates` (ALC-2545): the flat fields are the
 * TENANT-level default; `byEntity` carries per-entity overrides, all-or-nothing (an entity
 * override replaces the whole stream set for that entity, never a partial patch).
 * Resolution order per entity: entity override → tenant default → undefined (no setting
 * at all). See `resolvePaymentCalendarForEntity`.
 */
export interface PaymentCalendar {
  advance?: PaymentCalendarStream;
  proLabore?: PaymentCalendarStream;
  autonomous?: PaymentCalendarStream;
  byEntity?: EntityPaymentCalendarOverride[];
}

/** One entity's `paymentCalendar` override (ALC-3560). All-or-nothing, mirrors `EntityEncargosRateOverride`. */
export interface EntityPaymentCalendarOverride {
  entityId: string;
  advance?: PaymentCalendarStream;
  proLabore?: PaymentCalendarStream;
  autonomous?: PaymentCalendarStream;
}

export interface HcmPayrollGlSettings {
  splitBy?: HcmPayrollSplitBy;
  payrollResultRootAccountIds?: string[];
  /** Default salary payment schedule when approving payroll (overridable per run). */
  salaryPaymentRule?: PayrollSalaryPaymentRule;
  /** Rubric provider codes mapped to GL transit destinations (deductions 240, 259, etc.). */
  deductionRubricMappings?: PayrollDeductionRubricMapping[];
  /** Configured encargo rates for internal provision accrual (contador-defined). */
  encargosRates?: PayrollEncargosRates;
  /** Municipal ISS payment day for RPA receipts (1–28, default 10). */
  autonomousIssDueDay?: number;
  /** Tenant vendor code for the municipality receiving withheld RPA ISS. */
  autonomousIssVendorCode?: string;
  /** Optional tenant override for the dated statutory CI INSS ceiling. */
  inssCeilingAmount?: number;
  /** Declared payment pattern for advance/pró-labore/autonomous streams (ALC-3560). */
  paymentCalendar?: PaymentCalendar;
  /**
   * Tenant classifications of earning / employer-charge rubrics by provider code
   * (ADR 0022 decision 4). Deductions keep using `deductionRubricMappings`.
   */
  rubricClassOverrides?: PayrollRubricClassOverride[];
}

/** `dayOfMonth` accepts the full 1–31 range; the engine clamps it to the competência's actual length. */
const PaymentCalendarStreamSchema = z.object({
  enabled: z.boolean(),
  dayOfMonth: z.number().int().min(1).max(31),
});

export const HcmPayrollGlSettingsSchema = z.object({
  splitBy: z.enum(['COST_CENTER', 'DEPARTMENT']).optional(),
  payrollResultRootAccountIds: z.array(z.string().uuid()).optional(),
  salaryPaymentRule: z
    .enum([
      PayrollSalaryPaymentRule.FIFTH_BUSINESS_DAY,
      PayrollSalaryPaymentRule.LAST_DAY_OF_MONTH,
      PayrollSalaryPaymentRule.FIRST_DAY_OF_NEXT_MONTH,
      PayrollSalaryPaymentRule.FIFTH_CALENDAR_DAY,
      PayrollSalaryPaymentRule.LAST_DAY_OF_REFERENCE_MONTH,
    ])
    .optional(),
  deductionRubricMappings: z
    .array(
      z
        .object({
          providerCode: z.string().trim().min(1).max(50),
          rubricName: z.string().trim().min(1).max(200).optional(),
          entityId: z.string().uuid().optional(),
          versionId: z.string().uuid().optional(),
          destinationType: z.enum([
            PayrollDeductionDestinationType.TAX,
            PayrollDeductionDestinationType.REPASSE,
            PayrollDeductionDestinationType.EXPENSE_RECOVERY,
            PayrollDeductionDestinationType.ASSET_SETTLEMENT,
          ]),
          glAccountId: z.string().uuid().optional(),
          grouping: z
            .enum([PayrollDeductionGrouping.GROUPED, PayrollDeductionGrouping.INDIVIDUAL])
            .optional(),
          generatesBill: z.boolean().optional(),
          billSubType: z
            .enum([
              PayrollBillSubType.SALARY,
              PayrollBillSubType.ADVANCE,
              PayrollBillSubType.PRO_LABORE,
              PayrollBillSubType.RPA,
              PayrollBillSubType.TERMINATION_NET,
              PayrollBillSubType.ALIMONY,
              PayrollBillSubType.CONSIGNED,
              PayrollBillSubType.UNION,
              PayrollBillSubType.TAX,
            ])
            .optional(),
          payeeVendorId: z.string().uuid().optional(),
        })
        .refine((m) => !m.generatesBill || m.billSubType != null, {
          message: 'billSubType is required when generatesBill is true',
        })
        .refine((m) => m.versionId == null || m.entityId != null, {
          message: 'entityId is required when versionId is set',
          path: ['entityId'],
        }),
    )
    .superRefine((mappings, context) => {
      const seen = new Set<string>();
      mappings.forEach((mapping, index) => {
        const key = `${mapping.providerCode}\u0000${mapping.entityId ?? ''}\u0000${mapping.versionId ?? ''}`;
        if (seen.has(key)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Duplicate providerCode mapping for the same entity and version scope',
            path: [index, 'providerCode'],
          });
          return;
        }
        seen.add(key);
      });
    })
    .optional(),
  encargosRates: z
    .object({
      // Tenant-level default. Optional individually so a tenant may configure
      // ONLY `byEntity` overrides with no flat default — still backward
      // compatible with the legacy shape, where all four were always present
      // together.
      cpp: z.number().min(0).max(1).optional(),
      ratFap: z.number().min(0).max(1).optional(),
      terceiros: z.number().min(0).max(1).optional(),
      fgts: z.number().min(0).max(1).optional(),
      byEntity: z
        .array(
          z.object({
            entityId: z.string().uuid(),
            effectiveFrom: z
              .string()
              .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'effectiveFrom must be a YYYY-MM month key'),
            cpp: z.number().min(0).max(1),
            ratFap: z.number().min(0).max(1),
            terceiros: z.number().min(0).max(1),
            fgts: z.number().min(0).max(1),
          }),
        )
        .max(200)
        .optional()
        .refine(
          (overrides) => {
            if (!overrides) return true;
            const seen = new Set<string>();
            for (const override of overrides) {
              const key = `${override.entityId}::${override.effectiveFrom}`;
              if (seen.has(key)) return false;
              seen.add(key);
            }
            return true;
          },
          { message: 'byEntity must not repeat the same entityId + effectiveFrom' },
        ),
    })
    .optional(),
  autonomousIssDueDay: z.number().int().min(1).max(28).default(10),
  autonomousIssVendorCode: z.string().trim().min(1).max(50).optional(),
  inssCeilingAmount: z.number().positive().optional(),
  paymentCalendar: z
    .object({
      advance: PaymentCalendarStreamSchema.optional(),
      proLabore: PaymentCalendarStreamSchema.optional(),
      autonomous: PaymentCalendarStreamSchema.optional(),
      byEntity: z
        .array(
          z.object({
            entityId: z.string().uuid(),
            advance: PaymentCalendarStreamSchema.optional(),
            proLabore: PaymentCalendarStreamSchema.optional(),
            autonomous: PaymentCalendarStreamSchema.optional(),
          }),
        )
        .max(200)
        .optional()
        .refine(
          (overrides) => {
            if (!overrides) return true;
            const seen = new Set<string>();
            for (const override of overrides) {
              if (seen.has(override.entityId)) return false;
              seen.add(override.entityId);
            }
            return true;
          },
          { message: 'byEntity must not repeat the same entityId' },
        ),
    })
    .optional(),
  rubricClassOverrides: z
    .array(
      z.object({
        providerCode: z.string().trim().min(1).max(50),
        rubricName: z.string().trim().min(1).max(200).optional(),
        side: z.enum([
          PayrollRubricOverrideSide.EARNING,
          PayrollRubricOverrideSide.EMPLOYER_CHARGE,
        ]),
        class: z.string().trim().min(1).max(50),
        entityId: z.string().uuid().optional(),
      }),
    )
    .max(2000)
    .superRefine((overrides, context) => {
      const seen = new Set<string>();
      overrides.forEach((override, index) => {
        const key = `${override.providerCode}\u0000${override.side}\u0000${override.entityId ?? ''}`;
        if (seen.has(key)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Duplicate rubric class override for the same providerCode, side and entity',
            path: [index, 'providerCode'],
          });
          return;
        }
        seen.add(key);
      });
    })
    .optional(),
});

export interface TenantSettingResponse {
  tenantId: string;
  settingType: SettingType;
  settingConfig:
    | string[]
    | ReimbursementIntakeField[]
    | ArCollectionSettings
    | ApprovalGlobalSettings
    | BillingArForecastSettings
    | BudgetGlobalSettings
    | ProcurementGlobalSettings
    | BillingSaasMetricsSettings
    | BusinessContextSetting
    | HcmPayrollGlSettings
    | ApBillIngestionSettings
    | ArInvoiceApprovalSettings
    | null;
  isActive: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface TenantSettingHistoryResponse {
  tenantId: string;
  historyId: string;
  settingType: SettingType;
  settingConfig:
    | string[]
    | ReimbursementIntakeField[]
    | ArCollectionSettings
    | ApprovalGlobalSettings
    | BillingArForecastSettings
    | BudgetGlobalSettings
    | ProcurementGlobalSettings
    | BillingSaasMetricsSettings
    | BusinessContextSetting
    | HcmPayrollGlSettings
    | ApBillIngestionSettings
    | ArInvoiceApprovalSettings
    | null;
  isActive: boolean;
  description: string | null;
  createdAt: string;
  createdBy: string | null;
}
